// AgentCatalogProvider — new-arch agent catalog provider for Story 10.
//
// Wraps useAgentsQuery and exposes the result through AgentCatalogContext so
// components deeper in the tree can read agents without calling the hook
// themselves.  Mounts its own AgentsApiProvider with a module-level singleton
// client so callers do not need to wire one explicitly.
//
// refreshCatalog() calls queryClient.invalidateQueries on the agents cache key.
// Consumers stay subscribed — TanStack Query refetches in the background and
// the context value updates on the next render.

/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createApiClient } from '@/shared/api';
import { getToken } from '@/shared/auth';
import { readEnv } from '@/shared/config';
import { log } from '@/shared/logger';
import { AgentsApiProvider, useAgentsQuery, agentKeys } from '@/entities/agent';
import type { AgentsApiClient } from '@/entities/agent';
import type { AgentView, DeniedAgentView } from '@/entities/agent';

export type AgentCatalogContextValue = {
  agents: ReadonlyArray<AgentView>;
  deniedAgents: ReadonlyArray<DeniedAgentView>;
  agentsLoading: boolean;
  agentsError: Error | null;
  refreshCatalog: () => void;
};

const AgentCatalogContext = createContext<AgentCatalogContextValue | null>(null);

const authProvider = { getToken };

let _agentsClient: AgentsApiClient | null = null;

function getAgentsClient(): AgentsApiClient {
  if (!_agentsClient) {
    const baseUrl = readEnv('VITE_API_BASE_URL') ?? '';
    const rawClient = createApiClient({ baseUrl, auth: authProvider });
    _agentsClient = {
      fetchCatalog: (signal) => rawClient.request({ path: '/api/v1/agents', signal }),
    };
  }
  return _agentsClient;
}

function AgentCatalogInner({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useAgentsQuery();
  const agentsError = useMemo(
    () => (error instanceof Error ? error : error != null ? new Error(String(error)) : null),
    [error],
  );

  useEffect(() => {
    if (agentsError === null) return;
    log.error('agent-catalog: failed to load', agentsError, {
      route: '/api/v1/agents',
      extra: {
        queryKey: agentKeys.catalog().join(':'),
      },
    });
  }, [agentsError]);

  const value = useMemo(
    (): AgentCatalogContextValue => ({
      agents: data?.agents ?? [],
      deniedAgents: data?.deniedAgents ?? [],
      agentsLoading: isLoading,
      agentsError,
      refreshCatalog: () => {
        void queryClient.invalidateQueries({ queryKey: agentKeys.catalog() });
      },
    }),
    [data, isLoading, agentsError, queryClient],
  );

  return (
    <AgentCatalogContext.Provider value={value}>
      {children}
    </AgentCatalogContext.Provider>
  );
}

export interface AgentCatalogProviderProps {
  children: ReactNode;
  /** Override the API client — intended for tests only. */
  client?: AgentsApiClient;
}

export function AgentCatalogProvider({ children, client }: AgentCatalogProviderProps) {
  return (
    <AgentsApiProvider client={client ?? getAgentsClient()}>
      <AgentCatalogInner>{children}</AgentCatalogInner>
    </AgentsApiProvider>
  );
}

export function useAgentCatalog(): AgentCatalogContextValue {
  const ctx = useContext(AgentCatalogContext);
  if (!ctx) throw new Error('useAgentCatalog must be inside AgentCatalogProvider');
  return ctx;
}
